import {
    OwnBankAccount,
    renderBankRecipientValue,
    renderBankSenderValue,
    renderCardRecipientValue,
    renderCardSenderValue,
} from './table';

export interface SenderRecipientLabels {
    sender: string;
    recipient: string;
}

// The two lines of the "Sender / Recipient" column, resolved once so that the table cell and
// the transaction modal always agree on what a transaction shows. The special cases come first
// because their bankTransactionType alone does not describe the parties.
export const getSenderRecipientLabels = (
    record: any,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
): SenderRecipientLabels => {
    const bankTransaction = record?.bankTransaction;
    if (bankTransaction?.description === 'Release refunded amount') {
        return {
            sender: 'Scheme settlement',
            recipient: record?.prefundBalanceAfter ? 'Pre-fund' : 'Settlement',
        };
    }
    switch (bankTransaction?.bankTransactionType) {
        case 'settlement':
            return { sender: 'Settlement', recipient: 'Scheme settlement' };
        case 'bankFee':
            return { sender: 'Pre-fund', recipient: 'Bank settlement' };
        case 'rebate':
            return { sender: 'Monavate', recipient: 'Pre-fund' };
        default:
            break;
    }
    if (record?.type === 'bank') {
        return {
            sender: renderBankSenderValue(record, ownAccounts, clientName) || '',
            recipient: renderBankRecipientValue(record, ownAccounts, clientName) || '',
        };
    }
    return {
        sender: renderCardSenderValue(record) || '',
        recipient: renderCardRecipientValue(record) || '',
    };
};

export const SETTLEMENT_PARTY = 'Settlement';
export const PREFUND_PARTY = 'Pre-fund';

// The Sender / Recipient filter matches either line of the column, exactly as the table renders
// them: "Settlement" keeps every row where Settlement is the sender or the recipient (locks and
// unlocks of funds, scheme settlement, outgoing bank transfers), "Pre-fund" every row where
// Pre-fund is the sender or the recipient (locks and unlocks, money in and out, bank fees,
// rebates, incoming transfers). QA (18 Sep): "filter by both lines".
export const matchesSenderRecipient = (
    record: any,
    party: string,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
): boolean => {
    const { sender, recipient } = getSenderRecipientLabels(record, ownAccounts, clientName);
    return sender === party || recipient === party;
};

// IBANs are pasted with spaces as often as without, and an account number is typed by hand,
// so both sides of the comparison are stripped down before matching.
const normalizeAccountSearch = (value?: string): string =>
    (value || '').replace(/\s/g, '').toLowerCase();

// FAST payments carry no IBAN: their parties are identified by sort code and account number,
// so the search box has to match the account number as well.
const matchesAccount = (record: any, search: string): boolean => {
    const wanted = normalizeAccountSearch(search);
    return [
        record?.bankTransaction?.debtor?.iban,
        record?.bankTransaction?.creditor?.iban,
        record?.bankTransaction?.debtor?.accountNumber,
        record?.bankTransaction?.creditor?.accountNumber,
    ].some((value?: string) => !!value && normalizeAccountSearch(value) === wanted);
};

// The transactions API has no Sender / Recipient or account parameter, so these two filters are
// applied on the client to the rows the API returned for the server-side filters.
export const applyClientSideTransactionFilters = (
    transactions: any[],
    filters: Record<string, any> | undefined,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
): any[] => {
    let result = transactions || [];
    const search = filters?.iban;
    if (search) {
        result = result.filter((item) => matchesAccount(item, search));
    }
    const party = filters?.senderRecipient;
    if (party) {
        result = result.filter((item) =>
            matchesSenderRecipient(item, party, ownAccounts, clientName),
        );
    }
    return result;
};
